import React, { useState } from 'react';
import { packages } from '../mock';
import { Check, Clock, DollarSign, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const Packages = () => {
  const [activeTab, setActiveTab] = useState('detailing');

  const categories = {
    detailing: packages.filter(p => [1, 2, 3, 4].includes(p.id)),
    ceramic: packages.filter(p => [5, 6].includes(p.id))
  };

  return (
    <section id="packages" className="py-20 bg-zinc-900 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-4xl md:text-5xl font-bold text-white">
            ALL <span className="bg-gradient-to-r from-orange-400 to-orange-600 bg-clip-text text-transparent">PACKAGES</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-2xl mx-auto">
            Choose the perfect package for your vehicle's needs
          </p>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="detailing" className="w-full" onValueChange={setActiveTab}>
          <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 bg-zinc-800 p-1 rounded-lg">
            <TabsTrigger
              value="detailing"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white text-gray-400 font-semibold py-3 rounded-md transition-all duration-300"
            >
              Mobile Detailing
            </TabsTrigger>
            <TabsTrigger
              value="ceramic"
              className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-orange-600 data-[state=active]:text-white text-gray-400 font-semibold py-3 rounded-md transition-all duration-300"
            >
              Ceramic Coatings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="detailing" className="mt-0">
            <PackageGrid packages={categories.detailing} />
          </TabsContent>

          <TabsContent value="ceramic" className="mt-0">
            <PackageGrid packages={categories.ceramic} />
          </TabsContent>
        </Tabs>
      </div>
    </section>
  );
};

const PackageGrid = ({ packages }) => {
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
      {packages.map((pkg, index) => (
        <Card
          key={pkg.id}
          className={`group relative bg-zinc-800/50 border-zinc-700 hover:border-orange-500 overflow-hidden transform hover:scale-105 hover:-translate-y-2 transition-all duration-500 ${
            pkg.tag ? 'ring-2 ring-orange-500' : ''
          }`}
          style={{ animationDelay: `${index * 100}ms` }}
        >
          {/* Tag */}
          {pkg.tag && (
            <div className="absolute top-0 right-0 bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 text-xs font-bold rounded-bl-xl shadow-lg">
              {pkg.tag}
            </div>
          )}

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Package Name */}
            <h3 className="text-2xl font-bold text-white group-hover:text-orange-500 transition-colors duration-300 pr-20">
              {pkg.name}
            </h3>

            {/* Price & Duration */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-orange-500" />
                <span className="text-2xl font-bold text-white">{pkg.price}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-400">
                <Clock className="w-4 h-4" />
                <span className="text-sm">{pkg.duration}</span>
              </div>
            </div>

            {/* Features */}
            <div className="space-y-3 py-4 border-t border-zinc-700">
              {pkg.features.map((feature, idx) => (
                <div key={idx} className="flex items-start gap-2">
                  <Check className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-300 text-sm leading-relaxed">{feature}</span>
                </div>
              ))}
            </div>

            {/* Book Button */}
            <Button
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-4 rounded-lg transform hover:scale-105 transition-all duration-300 shadow-lg group-hover:shadow-orange-500/50"
            >
              Book Now
            </Button>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default Packages;
